/**
 * Button Component
 * A versatile button component with multiple variants, sizes, and full accessibility support
 */

import React from 'react';
import styled from 'styled-components';

// ============================================================================
// Types & Interfaces
// ============================================================================

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ComponentPropsWithoutRef<'button'> {
  /** Visual style variant */
  variant?: ButtonVariant;
  /** Size of the button */
  size?: ButtonSize;
  /** Whether button should take full width of container */
  fullWidth?: boolean;
  /** Loading state - shows spinner and disables interaction */
  loading?: boolean;
  /** Icon to display before text */
  startIcon?: React.ReactNode;
  /** Icon to display after text */
  endIcon?: React.ReactNode;
  /** Button content */
  children: React.ReactNode;
  /** Underlying element type */
  as?: 'button' | 'a';
  /** Anchor href when rendering as link */
  href?: string;
  /** Anchor target when rendering as link */
  target?: React.ComponentPropsWithoutRef<'a'>['target'];
  /** Anchor rel when rendering as link */
  rel?: React.ComponentPropsWithoutRef<'a'>['rel'];
}

// ============================================================================
// Styled Components
// ============================================================================

const ButtonBase = styled.button<{
  $variant: ButtonVariant;
  $size: ButtonSize;
  $fullWidth: boolean;
  $loading: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: ${({ $size }) =>
    $size === 'sm' ? '2rem' : $size === 'lg' ? '3rem' : '2.5rem'};
  padding: 0.5rem ${({ $size }) => ($size === 'sm' ? '0.75rem' : '1rem')};
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  font: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.4;
  text-decoration: none;
  cursor: pointer;
  white-space: normal;
  background: ${({ $variant }) =>
    $variant === 'primary'
      ? 'var(--action-primary)'
      : $variant === 'secondary'
        ? 'var(--surface)'
        : 'transparent'};
  color: ${({ $variant }) =>
    $variant === 'primary' ? 'var(--on-action)' : 'var(--text-primary)'};
  border-color: ${({ $variant }) =>
    $variant === 'secondary' ? 'var(--border-strong)' : 'transparent'};
  transition:
    background-color var(--transition-fast),
    border-color var(--transition-fast);
  &:hover:not(:disabled):not([aria-disabled='true']) {
    background: ${({ $variant }) =>
      $variant === 'primary'
        ? 'var(--action-primary-hover)'
        : 'var(--interactive-hover)'};
  }
  &:focus-visible {
    outline: 2px solid var(--interactive-focus);
    outline-offset: 3px;
  }
  &:disabled,
  &[aria-disabled='true'] {
    opacity: 0.55;
    cursor: not-allowed;
  }
  &[aria-disabled='true'] {
    pointer-events: none;
  }
  @media (max-width: 767px) {
    min-height: 2.75rem;
  }
`;

const LoadingSpinner = styled.span`
  display: inline-block;
  width: 1em;
  height: 1em;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spin 0.75s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 1.2em;
    height: 1.2em;
  }
`;

// ============================================================================
// Component
// ============================================================================

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      loading = false,
      startIcon,
      endIcon,
      children,
      disabled,
      type = 'button',
      as = 'button',
      href,
      target,
      rel,
      'aria-label': ariaLabel,
      ...rest
    },
    ref,
  ) => {
    // Combine disabled state with loading
    const isDisabled = disabled || loading;

    const anchorProps =
      as === 'a'
        ? {
            href: isDisabled ? undefined : href,
            target,
            rel,
          }
        : {};
    const buttonProps = as === 'button' ? { disabled: isDisabled } : {};

    return (
      <ButtonBase
        ref={ref}
        as={as}
        type={as === 'button' ? type : undefined}
        $variant={variant}
        $size={size}
        $fullWidth={fullWidth}
        $loading={loading}
        aria-label={ariaLabel}
        aria-disabled={isDisabled}
        {...anchorProps}
        {...buttonProps}
        {...rest}
      >
        {loading && <LoadingSpinner aria-hidden="true" />}
        {!loading && startIcon && (
          <IconWrapper aria-hidden="true">{startIcon}</IconWrapper>
        )}

        <span>{children}</span>

        {!loading && endIcon && (
          <IconWrapper aria-hidden="true">{endIcon}</IconWrapper>
        )}
      </ButtonBase>
    );
  },
);

Button.displayName = 'Button';
